import React, { useState } from "react";
import SubTaskListEditor from "../editors/SubTaskListEditor";
import SubTaskPickerModal from "../pickers/SubTaskPickerModal";

/**
 * Reusable Task form component
 * Used in both Registry and Project contexts
 * Includes nested SubTaskListEditor
 */
const TaskForm = ({
  value,
  onChange,
  mode = "project",
  showSaveToRegistry = false,
  disabled = false
}) => {
  const [showSubTaskPicker, setShowSubTaskPicker] = useState(false);

  const handleChange = (field, val) => {
    onChange({ ...value, [field]: val });
  };

  const handleSubTasksChange = (subTasks) => {
    handleChange("subTasks", subTasks);
  };

  const handleAddSubTasksFromRegistry = (selectedSubTasks) => {
    const newSubTasks = selectedSubTasks.map((st, index) => ({
      id: st._id,
      _id: st._id,
      name: st.name,
      description: st.description || "",
      weight: st.weight || st.defaultWeight || 1,
      estimatedHours: st.estimatedHours || st.defaultEstimatedHours || "",
      registryId: st._id,
      sortOrder: (value.subTasks?.length || 0) + index
    }));

    handleChange("subTasks", [...(value.subTasks || []), ...newSubTasks]);
  };

  const existingSubTaskIds = (value.subTasks || [])
    .map(st => st.registryId || st._id)
    .filter(Boolean);

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-1">Name *</label>
        <input
          type="text"
          value={value.name || ""}
          onChange={(e) => handleChange("name", e.target.value)}
          className="w-full px-3 py-2 border rounded"
          placeholder="e.g., Ground Preparation"
          disabled={disabled}
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Description</label>
        <textarea
          value={value.description || ""}
          onChange={(e) => handleChange("description", e.target.value)}
          className="w-full px-3 py-2 border rounded"
          rows={3}
          placeholder="Optional description..."
          disabled={disabled}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Weight</label>
          <input
            type="number"
            step="0.1"
            min="0"
            value={value.weight || ""}
            onChange={(e) => handleChange("weight", e.target.value ? parseFloat(e.target.value) : "")}
            className="w-full px-3 py-2 border rounded"
            placeholder="1.0"
            disabled={disabled}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Est. Hours</label>
          <input
            type="number"
            step="0.5"
            min="0"
            value={value.estimatedHours || ""}
            onChange={(e) => handleChange("estimatedHours", e.target.value ? parseFloat(e.target.value) : "")}
            className="w-full px-3 py-2 border rounded"
            placeholder="8.0"
            disabled={disabled}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Tags (comma-separated)</label>
        <input
          type="text"
          value={Array.isArray(value.tags) ? value.tags.join(", ") : (value.tags || "")}
          onChange={(e) => handleChange("tags", e.target.value)}
          className="w-full px-3 py-2 border rounded"
          placeholder="e.g., groundwork, excavation, foundation"
          disabled={disabled}
        />
      </div>

      <div className="border-t pt-3">
        <label className="flex items-start">
          <input
            type="checkbox"
            checked={value.showDescriptionInTimeReport || false}
            onChange={(e) => handleChange("showDescriptionInTimeReport", e.target.checked)}
            className="mt-1 mr-2"
            disabled={disabled}
          />
          <div>
            <div className="font-medium text-sm">Show description field in time reports</div>
            <div className="text-xs text-gray-600">
              When enabled, workers can add a description when logging time for this task
            </div>
          </div>
        </label>
        {value.showDescriptionInTimeReport && (
          <label className="flex items-start mt-3 ml-6">
            <input
              type="checkbox"
              checked={value.requireDescriptionInTimeReport || false}
              onChange={(e) => handleChange("requireDescriptionInTimeReport", e.target.checked)}
              className="mt-1 mr-2"
              disabled={disabled}
            />
            <div>
              <div className="font-medium text-sm">Require description in time reports</div>
              <div className="text-xs text-gray-600">
                When enabled, workers must enter a description when logging time for this task
              </div>
            </div>
          </label>
        )}
      </div>

      <div className="border-t pt-3">
        <label className="flex items-start">
          <input
            type="checkbox"
            checked={value.photosNotRequiredInTimeReport || false}
            onChange={(e) => handleChange("photosNotRequiredInTimeReport", e.target.checked)}
            className="mt-1 mr-2"
            disabled={disabled}
          />
          <div>
            <div className="font-medium text-sm">Photos not required in time reports</div>
            <div className="text-xs text-gray-600">
              When enabled, a time report needs no photos if it only contains tasks with this setting. If the report also has tasks that require photos, photos are still required.
            </div>
          </div>
        </label>
      </div>

      {showSaveToRegistry && mode === "project" && (
        <div className="border-t pt-3">
          <label className="flex items-start">
            <input
              type="checkbox"
              checked={value.saveToRegistry || false}
              onChange={(e) => handleChange("saveToRegistry", e.target.checked)}
              className="mt-1 mr-2"
              disabled={disabled}
            />
            <div>
              <div className="font-medium text-sm">Save to Registry</div>
              <div className="text-xs text-gray-600">
                Make this task reusable for future projects
              </div>
            </div>
          </label>
        </div>
      )}

      {/* SubTasks Section */}
      <div className="border-t pt-4">
        <SubTaskListEditor
          subTasks={value.subTasks || []}
          onChange={handleSubTasksChange}
          mode={mode}
          showSaveToRegistry={showSaveToRegistry}
          onAddFromRegistry={() => setShowSubTaskPicker(true)}
          disabled={disabled}
        />
      </div>

      {/* SubTask Picker Modal */}
      <SubTaskPickerModal
        isOpen={showSubTaskPicker}
        onClose={() => setShowSubTaskPicker(false)}
        onSelect={handleAddSubTasksFromRegistry}
        existingIds={existingSubTaskIds}
      />
    </div>
  );
};

export default TaskForm;
